import React, { useMemo, useState } from "react";
import type { BridgeQuestion, SchemaField } from "../../types/shared";
import buildQuestionnaire, { emitQuestionnaireAnswers, renderBackendQuestions } from "../../lib/bridgeQuestionnaire";
import PatientStepHeader from "./PatientStepHeader";
import QuestionAttachment from "./QuestionAttachment";
import InsuranceCardCapture from "./InsuranceCardCapture";
import {
  AUTO_ADVANCE_WIDGETS,
  clampStep,
  displayValue,
  factLabel,
  groupKnownQuestions,
  identifierTargets,
  nextLabel,
  oneQuestionPerScreen,
  routeCardCandidatesToQuestions,
  splitKnownAndToAnswer,
} from "../../lib/questionnaireSections";
import { FORMATS, formatValue, maskedChange, inferInputFormat } from "../../shared/lib/inputFormats";

/**
 * BridgeQuestionnaire — a clean, conditional questionnaire for the bridge patient
 * flow. Renders the question list the BACKEND already built (sealed into the
 * dispatch payload as `questions`) — the backend is the single source of truth
 * for which questions exist, their plain-language stems, Yes/No collapse,
 * audience filtering and conditional rules. This component only does the runtime
 * pieces: skip questions already answered this session and show/hide
 * conditionals live as the patient answers. Emits a { machine_name: value } map
 * for the bridge submission.
 *
 * Widgets (from the backend question model):
 *   yesno   Yes / No buttons — plus "Not applicable" when `targets.na` is set
 *           (the third member of a Yes/No/N-A radio; 2026-08-27, imp_d0b265ea)
 *   choice  one button per `options[]` entry (a radio group), single-select
 *   multi   one button per `options[]` entry (a checkbox group), multi-select
 *   toggle  a single "this applies" button (a lone checkbox / radio member)
 *   date / number / tel / email / textarea / text  inputs
 *
 * A question the backend flagged `attachment: true` (the printed ask says to
 * attach documentation) also gets an upload slot under its widget — camera or
 * file picker, sent through the sealed letter-upload path. It never gates
 * Continue: an unsent document is a gap the care team chases, not a lockout
 * (case imp_adc19063).
 *
 * Empty vs answered must be unmistakable (imp_c7f2fe98): inputs carry a light
 * neutral placeholder — never the question text — required questions wear a
 * "Required" tag, and after a Continue attempt every unanswered required card
 * is highlighted and the message says how many remain.
 *
 * Props:
 *   questions [{target,label,widget,targets?,options?,conditionalOn?}]  backend-built (preferred)
 *   fields    [{name,label,type,value,conditionalOn?}]  schema fields (seeded values + fallback)
 *   context   {trigger_base: "yes"|"no"}  physician-answered triggers (for conditionals)
 *   title     string
 *   onComplete(fieldValues)   {machine_name: value} for the answered/changed fields
 *   onBack()
 *
 * Back-compat: assignments sealed before the backend emitted `questions` pass
 * none, so this falls back to the legacy client builder over `fields`.
 */
const C = {
  bg: "#f5f0eb", panel: "#fff", ink: "#182a33", muted: "#5b6e7c", border: "#e6dccb",
  teal: "#1b5e6e", tealTint: "#e4ecee", alert: "#c25450", alertTint: "#fff6f5", placeholder: "#a9b3ba",
};
// Placeholders are a hint, not an answer: light grey, and never the question
// text (a dark question-as-placeholder read as "already answered").
const PLACEHOLDER_CSS = ".pq-input::placeholder{color:#a9b3ba;opacity:1}";
const ANSWER_HINT = "Type your answer";

type AnswerValue = string | string[];

function isAnswered(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && String(v).trim() !== "";
}

interface BridgeQuestionnaireProps {
  questions?: BridgeQuestion[] | null;
  fields?: SchemaField[];
  context?: Record<string, unknown>;
  /** One audience, or several — a single-signer session owns both `patient`
   *  and `plan_holder`. See lib/signerAudience. */
  audience?: string | string[] | null;
  title?: string;
  onComplete?: (fieldValues: Record<string, unknown>) => void;
  onBack?: () => void;
}

export default function BridgeQuestionnaire({ questions: backendQuestions = null, fields = [], context = {}, audience = null, title = "A few quick questions", onComplete, onBack }: BridgeQuestionnaireProps) {
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  // Set on the first Continue that finds a required question unanswered —
  // from then on those cards are highlighted until answered.
  const [attempted, setAttempted] = useState(false);

  const hasBackendModel = Array.isArray(backendQuestions) && backendQuestions.length > 0;
  const built = useMemo(
    () =>
      hasBackendModel
        // keepAnswered: the known facts are shown to confirm, not set aside.
        ? renderBackendQuestions(backendQuestions, { fields, answers, context, keepAnswered: true })
        : buildQuestionnaire(fields, { context, answers, audience }),
    [hasBackendModel, backendQuestions, fields, answers, context, audience]
  );
  const questions = built.questions;

  const valueOf = (q: BridgeQuestion): AnswerValue =>
    (answers[q.target as string] ?? (q.value as AnswerValue) ?? "") as AnswerValue;
  const setAnswer = (target: string, value: AnswerValue) => setAnswers((p) => ({ ...p, [target]: value }));

  // Two parts (Michael, 2026-10-08, imp_af790eed): what we still need, and
  // what we already hold, to confirm or edit. The questions come first and
  // the held details fold into one line (Michael, 2026-10-28, imp_6b80f8dc:
  // the details "take up a lot of space" while the questions were "squished
  // into the bottom"). A known question stays in its part while it is being
  // edited so the page does not reshuffle.
  const { known, toAnswer } = useMemo(() => splitKnownAndToAnswer(questions), [questions]);
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [reviewOpen, setReviewOpen] = useState(false);

  // The benefits-card reader (imp_e5900eef): offered whenever a plan
  // identifier the card prints is still empty. The reader returns level-tagged
  // candidates; routing onto the questionnaire's targets never overwrites a
  // box the person already filled.
  const idTargets = useMemo(() => identifierTargets(questions), [questions]);
  const byTarget = (t?: string) => questions.find((q) => q.target === t);
  const needsCard = [idTargets.plan, idTargets.person].some((t) => {
    const q = t ? byTarget(t) : undefined;
    return !!q && !isAnswered(valueOf(q));
  });
  const applyCardCandidates = (candidates: any[]): number => {
    const current: Record<string, unknown> = {};
    for (const t of [idTargets.plan, idTargets.person]) {
      const q = t ? byTarget(t) : undefined;
      if (t && q) current[t] = valueOf(q);
    }
    const patch = routeCardCandidatesToQuestions(candidates, idTargets, current);
    const n = Object.keys(patch).length;
    if (n) setAnswers((p) => ({ ...p, ...patch }));
    return n;
  };

  const answeredCount = toAnswer.filter((q) => isAnswered(valueOf(q))).length;
  const missingRequired = questions.filter((q) => !!q.required && !isAnswered(valueOf(q)));
  const isMissing = (q: BridgeQuestion) => attempted && !!q.required && !isAnswered(valueOf(q));
  // The folded details open by themselves when there is nothing else on the
  // page, or when one of them was emptied and is now owed.
  const detailsOpen = reviewOpen || toAnswer.length === 0 || known.some(isMissing);

  // One question per screen for the unknowns once there are more than three
  // (Michael, 2026-10-08): big targets on a phone, "n of m", Back, and
  // auto-advance when a choice is tapped. The known-facts list above stays a
  // list. The step is clamped on every render because a conditional question
  // can appear or disappear as answers change.
  const oneAtATime = oneQuestionPerScreen(toAnswer.length);
  const [step, setStep] = useState(0);
  const safeStep = clampStep(step, toAnswer.length);
  const current: BridgeQuestion | undefined = oneAtATime ? toAnswer[safeStep] : undefined;
  const isLast = safeStep >= toAnswer.length - 1;
  const goTo = (i: number) => setStep(clampStep(i, toAnswer.length));
  const answerAndMaybeAdvance = (q: BridgeQuestion, v: AnswerValue) => {
    setAnswer(q.target as string, v);
    if (oneAtATime && q === current && !isLast && AUTO_ADVANCE_WIDGETS.has(String(q.widget)) && isAnswered(v)) {
      window.setTimeout(() => setStep((s) => s + 1), 220);
    }
  };
  const nextStep = () => {
    if (current && current.required && !isAnswered(valueOf(current))) {
      setAttempted(true);
      return;
    }
    if (isLast) handleContinue();
    else goTo(safeStep + 1);
  };

  const cardBox = (
    <div style={{ background: C.tealTint, borderRadius: 14, padding: 16, marginBottom: 14 }}>
      <p style={{ color: C.ink, fontSize: 14, margin: "0 0 8px", lineHeight: 1.5 }}>
        Have your benefits card handy? Take a photo and we'll read the numbers for you.
      </p>
      <InsuranceCardCapture onExtracted={applyCardCandidates} />
    </div>
  );

  const renderCard = (q: BridgeQuestion) => {
    const missing = isMissing(q);
    return (
      <div
        key={q.target}
        id={`pq-${q.target}`}
        style={{
          background: missing ? C.alertTint : C.panel, borderRadius: 14, padding: 20, marginBottom: 14,
          boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
          border: `2px solid ${missing ? C.alert : "transparent"}`,
        }}
      >
        <label style={{ display: "flex", alignItems: "baseline", gap: 8, color: missing ? C.alert : C.ink, fontWeight: 600, fontSize: 16, marginBottom: 10 }}>
          <span style={{ flex: 1 }}>{q.label}</span>
          {q.required && (
            <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: C.alert, background: C.alertTint, border: `1px solid ${C.alert}`, borderRadius: 999, padding: "2px 8px" }}>
              Required
            </span>
          )}
        </label>
        <QuestionWidget q={q} value={valueOf(q)} onChange={(v) => answerAndMaybeAdvance(q, v)} />
        {q.attachment && (
          <QuestionAttachment
            questionTarget={String(q.target)}
            questionLabel={q.label}
          />
        )}
        {missing && (
          <p style={{ color: C.alert, fontSize: 13, margin: "8px 0 0" }}>Please answer this question.</p>
        )}
      </div>
    );
  };

  function handleContinue() {
    if (missingRequired.length > 0) {
      setAttempted(true);
      const first = missingRequired[0];
      if (oneAtATime) {
        // One per screen: go to the first unanswered required question
        // rather than scrolling to a card that is not on screen.
        const idx = toAnswer.indexOf(first);
        if (idx >= 0) {
          goTo(idx);
          return;
        }
      }
      // A known detail may sit in the folded list, which opens on this
      // render — scroll once it is there.
      window.setTimeout(() => {
        try {
          document.getElementById(`pq-${first.target}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* noop — scrolling is a courtesy */ }
      }, 0);
      return;
    }
    // Answered/changed questions, plus a blank for any conditional that is
    // hidden again (was answered, now N/A).
    const out = emitQuestionnaireAnswers(questions, built.hidden, answers);
    onComplete?.(out);
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", padding: 24, boxSizing: "border-box" }}>
      <style>{PLACEHOLDER_CSS}</style>
      <div style={{ maxWidth: 560, width: "100%" }}>
        <PatientStepHeader currentStep={1} />
        {onBack && (
          <button onClick={onBack} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "4px 0", marginBottom: 8 }}>← Back</button>
        )}
        <h2 style={{ color: C.ink, margin: "4px 0 4px" }}>{title}</h2>
        <p style={{ color: C.muted, marginTop: 0, marginBottom: 18, lineHeight: 1.5 }}>
          Only what we don't already have — we'll skip anything that doesn't apply to you.{" "}
          Questions marked <span style={{ color: C.alert, fontWeight: 600 }}>Required</span> must be answered before you can sign.
        </p>

        <section aria-label="Questions">
          {toAnswer.length === 0 ? (
            <div style={{ background: C.panel, borderRadius: 14, padding: 22, color: C.muted, boxShadow: "0 6px 24px rgba(0,0,0,0.06)", marginBottom: 14 }}>
              {known.length > 0
                ? "Nothing else to ask — check your details below, then continue."
                : "Nothing else to ask — continue to review and sign."}
            </div>
          ) : oneAtATime && current ? (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <span style={{ color: C.ink, fontSize: 13, fontWeight: 600 }}>
                  Question {safeStep + 1} of {toAnswer.length}
                </span>
                <span style={{ color: C.muted, fontSize: 13 }}>{answeredCount} answered</span>
              </div>
              <div style={{ height: 6, borderRadius: 999, backgroundColor: C.border, overflow: "hidden", marginBottom: 14 }}>
                <div style={{ height: "100%", width: `${Math.round(((safeStep + 1) / toAnswer.length) * 100)}%`, backgroundColor: C.teal, borderRadius: 999, transition: "width 250ms ease" }} />
              </div>
              {safeStep === 0 && needsCard && cardBox}
              {renderCard(current)}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => goTo(safeStep - 1)}
                  disabled={safeStep === 0}
                  style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 24, color: safeStep === 0 ? C.placeholder : C.ink, fontSize: 15, fontWeight: 600, padding: "12px 18px", cursor: safeStep === 0 ? "default" : "pointer" }}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  style={{ padding: "14px 26px", borderRadius: 24, fontSize: 15, fontWeight: 600, border: "none", color: "#fff", background: C.ink, cursor: "pointer" }}
                >
                  {nextLabel(isLast, isAnswered(valueOf(current)), !!current.required)}
                </button>
              </div>
            </div>
          ) : (
            <>
              {needsCard && cardBox}
              {toAnswer.map(renderCard)}
            </>
          )}
        </section>

        {known.length > 0 && (
          <section aria-label="Your details on file" style={{ marginTop: 18 }}>
            {/* One slim line until opened (imp_6b80f8dc). */}
            <button
              type="button"
              aria-expanded={detailsOpen}
              onClick={() => setReviewOpen(!detailsOpen)}
              style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                background: C.panel, border: `1px solid ${C.border}`, borderRadius: detailsOpen ? "12px 12px 0 0" : 12,
                padding: "11px 16px", cursor: "pointer", color: C.ink, fontSize: 14, textAlign: "left",
              }}
            >
              <span>
                Your details are on file <span style={{ color: C.muted }}>({known.length})</span>
              </span>
              <span style={{ color: C.teal, fontWeight: 600, flexShrink: 0 }}>{detailsOpen ? "Hide" : "Review ›"}</span>
            </button>
            {detailsOpen && (
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 12px 12px", padding: "4px 16px 10px" }}>
                <p style={{ color: C.muted, fontSize: 13, margin: "8px 0 4px", lineHeight: 1.45 }}>
                  Tap <strong>Edit</strong> on anything that's wrong.
                </p>
                {groupKnownQuestions(known).map((group) => (
                  <div key={group.title}>
                    <div style={{ color: C.teal, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", margin: "10px 0 2px" }}>
                      {group.title}
                    </div>
                    {group.items.map((q) => {
                      const open = !!editing[q.target as string];
                      const missing = isMissing(q);
                      return (
                        <div
                          key={q.target}
                          id={`pq-${q.target}`}
                          style={{ padding: "7px 0", borderTop: `1px solid ${C.border}`, background: missing ? C.alertTint : undefined }}
                        >
                          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ color: C.muted, fontSize: 13, flex: "1 1 40%", minWidth: 120 }}>{factLabel(q, fields)}</span>
                            {!open && (
                              <span style={{ color: C.ink, fontSize: 14, fontWeight: 600, flex: "1 1 40%", minWidth: 0, overflowWrap: "anywhere" }}>
                                {displayValue(q, valueOf(q)) || "—"}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => setEditing((p) => ({ ...p, [q.target as string]: !open }))}
                              style={{ background: "none", border: "1px solid transparent", color: C.teal, fontSize: 13, fontWeight: 600, padding: "2px 0", cursor: "pointer", flexShrink: 0, marginLeft: "auto" }}
                            >
                              {open ? "Done" : "Edit"}
                            </button>
                          </div>
                          {open && (
                            <div style={{ marginTop: 8 }}>
                              <QuestionWidget q={q} value={valueOf(q)} onChange={(v) => setAnswer(q.target as string, v)} />
                            </div>
                          )}
                          {missing && (
                            <p style={{ color: C.alert, fontSize: 13, margin: "6px 0 0" }}>Please answer this question.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {!(oneAtATime && current) && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
              <span style={{ color: C.muted, fontSize: 13 }}>
                {answeredCount} of {toAnswer.length} answered
              </span>
              <button
                onClick={handleContinue}
                style={{
                  padding: "14px 26px", borderRadius: 24, fontSize: 15, fontWeight: 600,
                  border: "none", color: "#fff", background: C.ink, cursor: "pointer",
                }}
              >
                Continue to review →
              </button>
            </div>
            {attempted && missingRequired.length > 0 && (
              <p style={{ color: C.alert, fontSize: 13, textAlign: "right", marginTop: 6 }}>
                {missingRequired.length} required answer{missingRequired.length > 1 ? "s" : ""} still missing — highlighted above.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface QuestionWidgetProps {
  q: BridgeQuestion;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
}

interface OptionButtonProps {
  label: string;
  on: boolean;
  onClick: () => void;
  grow?: boolean;
}

function OptionButton({ label, on, onClick, grow }: OptionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        flex: grow ? 1 : "0 1 auto", minWidth: 120, padding: "12px 14px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer",
        border: `2px solid ${on ? C.teal : C.border}`,
        background: on ? C.tealTint : "#fff",
        color: on ? C.teal : C.muted,
      }}
    >
      {label}
    </button>
  );
}

function QuestionWidget({ q, value, onChange }: QuestionWidgetProps) {
  const input: React.CSSProperties = {
    width: "100%", padding: "11px 12px", boxSizing: "border-box", fontSize: 16,
    border: `1.5px solid ${C.border}`, borderRadius: 9, color: C.ink, outline: "none", background: "#fffdfb",
  };
  const str = Array.isArray(value) ? "" : String(value ?? "");

  // Yes/No — plus "Not applicable" when the form's radio has a third member.
  if (q.widget === "yesno") {
    const opts: Array<{ key: string; label: string }> = [
      { key: "yes", label: "Yes" },
      { key: "no", label: "No" },
      ...(q.targets?.na ? [{ key: "na", label: "Not applicable" }] : []),
    ];
    return (
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {opts.map((opt) => (
          <OptionButton key={opt.key} label={opt.label} on={str === opt.key} onClick={() => onChange(opt.key)} grow />
        ))}
      </div>
    );
  }

  // A radio group: one button per option, single-select.
  if (q.widget === "choice") {
    return (
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {(q.options || []).map((opt) => (
          <OptionButton key={opt.target} label={opt.label} on={str === opt.target} onClick={() => onChange(str === opt.target ? "" : opt.target)} />
        ))}
      </div>
    );
  }

  // A checkbox group: one button per option, any number selected.
  if (q.widget === "multi") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {(q.options || []).map((opt) => {
          const on = arr.includes(opt.target);
          return (
            <OptionButton key={opt.target} label={opt.label} on={on}
              onClick={() => onChange(on ? arr.filter((t) => t !== opt.target) : [...arr, opt.target])} />
          );
        })}
      </div>
    );
  }

  // A lone tick box.
  if (q.widget === "toggle") {
    const on = str === "yes";
    return (
      <div style={{ display: "flex", gap: 10 }}>
        <OptionButton label={on ? "Yes — this applies" : "Tick if this applies"} on={on} onClick={() => onChange(on ? "" : "yes")} />
      </div>
    );
  }

  // As-you-type masking: prefer the backend-sealed input_format, else infer it
  // from the field shape. When present, this replaces the bare date/tel/email
  // inputs with a caret-safe masked text field (value stored in display form).
  const fmt =
    q.input_format ||
    inferInputFormat({ name: q.target, input_format: q.input_format, widget: q.widget, type: q.type });
  if (fmt && FORMATS[fmt]) {
    const displayVal = formatValue(fmt, str);
    return (
      <input
        type="text"
        className="pq-input"
        style={input}
        value={displayVal as string}
        onChange={(e) => maskedChange(fmt, displayVal, e, onChange)}
        placeholder={FORMATS[fmt].placeholder}
        inputMode={FORMATS[fmt].inputMode}
        maxLength={FORMATS[fmt].maxLength}
        autoComplete={FORMATS[fmt].autoComplete}
      />
    );
  }

  switch (q.widget) {
    case "date":
      return <input type="date" className="pq-input" style={input} value={str} onChange={(e) => onChange(e.target.value)} />;
    case "number":
      return <input type="number" inputMode="numeric" className="pq-input" style={input} value={str} onChange={(e) => onChange(e.target.value)} placeholder={ANSWER_HINT} />;
    case "tel":
      return <input type="tel" inputMode="tel" className="pq-input" style={input} value={str} onChange={(e) => onChange(e.target.value)} placeholder={ANSWER_HINT} />;
    case "email":
      return <input type="email" inputMode="email" className="pq-input" style={input} value={str} onChange={(e) => onChange(e.target.value)} placeholder={ANSWER_HINT} />;
    case "textarea":
      return <textarea rows={3} className="pq-input" style={{ ...input, resize: "vertical" }} value={str} onChange={(e) => onChange(e.target.value)} placeholder={ANSWER_HINT} />;
    default:
      return <input type="text" className="pq-input" style={input} value={str} onChange={(e) => onChange(e.target.value)} placeholder={ANSWER_HINT} />;
  }
}
